import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center cs-custom-cta justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        ghost:
          'cursor-pointer disabled:cursor-not-allowed disabled:opacity-80 w-fit text-text-strong hover:text-text-light bg-transparent',

        PrimaryFillButton:
          'cs-button bg-amethyst-accent text-gray975-white hover:bg-amethyst-light active:bg-amethyst-light disabled:bg-border-light disabled:text-text-default-soft disabled:cursor-not-allowed cursor-pointer px-3 py-4 transition-all duration-300 ease-in-out',

        SecondaryFillButton:
          'cs-button bg-surface-4 text-text-default-strong btn-secondary-fill-hover active:bg-surface-4 disabled:bg-border-light disabled:text-text-default-soft disabled:cursor-not-allowed cursor-pointer px-3 py-4 transition-all duration-300 ease-in-out',

        OpacityButton:
          'cs-button bg-opacity-button text-white hover:bg-surface-4 active:bg-surface-4 cursor-pointer px-3 py-4 transition-all duration-300 ease-in-out',

        UnderlineButton:
          'cs-m-body bg-transparent text-amethyst-accent border-b-1 border-border-amethyst-light hover:border-amethyst-accent active:border-amethyst-accent cursor-pointer py-1 px-0 transition-all duration-300 ease-in-out',

        ButtonContainer:
          'cs-m-body bg-transparent text-text-light hover:text-text-strong border-b-1 border-stroke-medium hover:border-stroke-strong active:border-stroke-strong cursor-pointer py-1 px-0 transition-all duration-300 ease-in-out',

        InlinePrimary:
          'cs-m-body bg-transparent text-amethyst-accent underline underline-offset-4 hover:font-bold active:font-bold cursor-pointer py-0 px-0 transition-all duration-300 ease-in-out',

        InlineSecondary:
          'text-base bg-transparent text-text-light underline underline-offset-4 hover:font-bold group-hover:text-text-strong  group-hover:font-bold active:font-bold cursor-pointer py-0 px-0 transition-all duration-300 ease-in-out leading-[150%]',

        link: 'bg-transparent text-xs cursor-pointer',

        DocSecondaryBorder:
          'cs-button text-white hover:bg-gray-900 hover:text-gray-25 border-2 text-gray-900 border-gray-900 active:bg-gray-900 active:text-gray-25 disabled:bg-border-light disabled:text-text-default-soft disabled:cursor-not-allowed cursor-pointer px-3 py-4 transition-all duration-300 ease-in-out',

        DocWhiteSecondary:
          'cs-button bg-white border border-black text-gray-600 hover:bg-gray-25 active:bg-white disabled:bg-border-light disabled:text-text-default-soft disabled:cursor-not-allowed cursor-pointer px-3 py-4 transition-all duration-300 ease-in-out',

        DocsPrimaryFillButton:
          'cs-button bg-amethyst-accent hover:text-gray-900 text-gray-25 hover:bg-amethyst-light active:bg-amethyst-light disabled:bg-border-light disabled:text-text-default-soft disabled:cursor-not-allowed cursor-pointer px-3 py-4 transition-all duration-300 ease-in-out',
      },
      size: {
        default: 'h-10 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-8 px-6 has-[>svg]:px-4',
        icon: ' size-9',
      },
    },
    defaultVariants: {
      variant: 'PrimaryFillButton',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      aria-label={props['aria-label']}
      role={props.role ? props.role : 'button'}
      className={cn(buttonVariants({ variant, size }), className)}
      tabIndex={-1}
      {...props}
    >
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
